import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getWorkoutPlans } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { Loader2, Plus, Activity, Calendar, ChevronRight, Clock, CheckCircle2, CircleDashed, Filter } from 'lucide-react';

const HistoryPage = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [filter, setFilter] = useState('all'); // all, active, completed

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const data = await getWorkoutPlans();
        setPlans(data);
      } catch (error) {
        console.error('Error fetching plans:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredPlans = plans.filter(plan => {
    if (filter === 'all') return true;
    const planStatus = plan.status || 'active';
    return planStatus === filter;
  });

  const activePlans = plans.filter(p => (p.status || 'active') === 'active').length;
  const completedPlans = plans.filter(p => p.status === 'completed').length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="history-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 
              className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-2"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              {t('historyTitle')} <span className="text-[#32CD32]">{t('plansWord')}</span>
            </h1>
            <p className="text-gray-400">
              {t('historyDesc')}
            </p>
          </div>
          <Link to="/generate">
            <Button className="btn-primary px-6 py-3 rounded-full" data-testid="new-plan-btn">
              <Plus className="mr-2 w-4 h-4" />
              {t('generateNewPlan')}
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        {plans.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="py-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#32CD32]/10 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-[#32CD32]" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{plans.length}</p>
                  <p className="text-[#A1A1AA] text-sm">{t('totalPlans')}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="py-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <CircleDashed className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{activePlans}</p>
                  <p className="text-[#A1A1AA] text-sm">{t('activePlans')}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-white/10">
              <CardContent className="py-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{completedPlans}</p>
                  <p className="text-[#A1A1AA] text-sm">{t('completedPlansLabel')}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Buttons */}
        {plans.length > 0 && (
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-gray-400 text-sm mr-2">{t('filterLabel')}</span>
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                filter === 'all' 
                  ? 'bg-[#32CD32] text-black font-medium' 
                  : 'bg-white/5 text-gray-300 hover:bg-white/10'
              }`}
              data-testid="filter-all"
            >
              {t('filterAll')} ({plans.length})
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                filter === 'active' 
                  ? 'bg-blue-500 text-white font-medium' 
                  : 'bg-white/5 text-gray-300 hover:bg-white/10'
              }`}
              data-testid="filter-active"
            >
              {t('filterActive')} ({activePlans})
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                filter === 'completed' 
                  ? 'bg-green-500 text-white font-medium' 
                  : 'bg-white/5 text-gray-300 hover:bg-white/10'
              }`}
              data-testid="filter-completed"
            >
              {t('filterCompleted')} ({completedPlans})
            </button>
          </div>
        )}

        {plans.length === 0 ? (
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="py-16 text-center">
              <Activity className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 
                className="text-xl font-bold mb-2 uppercase"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                {t('noPlansFound')}
              </h3>
              <p className="text-gray-500 mb-6">
                {t('createFirstPlanDesc')}
              </p>
              <Link to="/generate">
                <Button className="btn-primary px-8 py-4 rounded-full" data-testid="create-first-plan-btn">
                  <Plus className="mr-2 w-5 h-5" />
                  {t('createFirstPlan')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : filteredPlans.length === 0 ? (
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="py-16 text-center">
              <Filter className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 
                className="text-xl font-bold mb-2 uppercase"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                {filter === 'completed' ? t('noCompletedPlans') : t('noActivePlans')}
              </h3>
              <p className="text-gray-500 mb-6">
                {filter === 'completed' ? t('noneCompletedYet') : t('allCompleted')}
              </p>
              <Button 
                variant="outline" 
                className="border-white/20"
                onClick={() => setFilter('all')}
              >
                {t('seeAllPlans')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPlans.map((plan, index) => {
              const isCompleted = plan.status === 'completed';
              const isSuperseded = plan.status === 'superseded';
              return (
                <Link 
                  key={plan.id} 
                  to={`/history/${plan.id}`}
                  className="block opacity-0 animate-fadeInUp"
                  style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
                  data-testid={`history-plan-${plan.id}`}
                >
                  <Card className={`bg-[#121212] border-white/10 card-hover h-full ${isCompleted ? 'border-l-4 border-l-green-500' : ''}`}>
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isCompleted ? 'bg-green-500/10' : 'bg-[#32CD32]/10'
                        }`}>
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          ) : (
                            <Activity className="w-5 h-5 text-[#32CD32]" />
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {isCompleted && (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
                              {t('completedLabel')}
                            </span>
                          )}
                          {isSuperseded && (
                            <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full" data-testid={`superseded-badge-${plan.id}`}>
                              {t('supersededLabel')}
                            </span>
                          )}
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        </div>
                      </div>
                      <CardTitle 
                        className="text-lg uppercase tracking-tight"
                        style={{ fontFamily: 'Barlow Condensed' }}
                      >
                        {t('planWeeks').replace('{weeks}', plan.duration_weeks)}
                      </CardTitle>
                      {plan.focus_area && (
                        <CardDescription className="text-[#32CD32]">
                          {t('focusLabel')}: {plan.focus_area}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4 text-sm text-[#A1A1AA]">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(plan.created_at)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {plan.duration_weeks} {t('weeksShort')}
                        </div>
                      </div>
                      {isCompleted && plan.completed_at && (
                        <div className="mt-3 pt-3 border-t border-white/5">
                          <p className="text-xs text-green-400 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" />
                            {t('completedOn')} {formatDate(plan.completed_at)}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default HistoryPage;
